import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const STORAGE_KEY = "eazy_chat_conversation_id";
const STATUS_POLL_MS = 15000; // lightweight badge check while the panel is closed
const OPEN_POLL_MS = 5000; // full thread refresh while the panel is open

export function useChatWidget() {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [messages, setMessages] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  // Badge polling: only touches the read-only /status endpoint, so it never marks
  // messages as seen before the visitor actually opens the panel.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const poll = async () => {
      if (openRef.current) return; // full thread polling below already covers this
      try {
        const { data } = await api.get(`/chat/${conversationId}/status`);
        if (!cancelled) setHasUnread(!!data.unread_by_visitor);
      } catch {
        // conversation may have been cleared server-side — ignore, non-critical
      }
    };

    poll();
    const interval = setInterval(poll, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversationId]);

  // Full-thread polling while the panel is open.
  useEffect(() => {
    if (!open || !conversationId) return;
    let cancelled = false;
    let firstLoad = true;

    const poll = async () => {
      if (firstLoad) setLoading(true);
      try {
        const { data } = await api.get(`/chat/${conversationId}/messages`);
        if (cancelled) return;
        setMessages(data.items || []);
        setHasUnread(false);
      } catch {
        // ignore transient errors during polling
      } finally {
        if (firstLoad) {
          setLoading(false);
          firstLoad = false;
        }
      }
    };

    poll();
    const interval = setInterval(poll, OPEN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, conversationId]);

  const startConversation = useCallback(async ({ name, email, message }) => {
    setSending(true);
    try {
      const { data } = await api.post("/chat/start", { name, email: email || undefined, message });
      localStorage.setItem(STORAGE_KEY, data.conversation_id);
      setConversationId(data.conversation_id);
      return data.conversation_id;
    } finally {
      setSending(false);
    }
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!conversationId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/${conversationId}/messages`, { message: text });
      setMessages((m) => [...m, data]);
    } finally {
      setSending(false);
    }
  }, [conversationId]);

  const toggleOpen = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  return {
    open, toggleOpen,
    conversationId, hasUnread,
    messages, loading, sending,
    startConversation, sendMessage,
  };
}
